import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { COUNTRY_DIAL_LIST, getCountryDialList, normalizeForSearch } from '../../countryData';
import { useT, useWidgetLanguage } from '../../i18n';
import { eventPathContains } from '../../utils';
import styles from './PhoneInput.module.css';

// Parsing and dial-code lookup are language-independent (they read `code` and
// `dial`), so the module-level list serves them; only the rendered names below
// follow the visitor's language.
const countryDialList = COUNTRY_DIAL_LIST;

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  hasError?: boolean;
  /** Country preselecting the dial prefix; never fills the value, so a required phone still needs one. */
  defaultCountry?: string;
}

/** Parse a phone value string into country code + local number */
function parsePhoneValue(val: string): { code: string; number: string } {
  if (!val) return { code: 'US', number: '' };
  for (const country of countryDialList) {
    const dialNormalized = country.dial.replace(/-/g, '');
    if (val.startsWith(dialNormalized) || val.startsWith(country.dial)) {
      const numberPart = val.startsWith(country.dial)
        ? val.slice(country.dial.length)
        : val.slice(dialNormalized.length);
      return { code: country.code, number: numberPart.trim() };
    }
  }
  return { code: 'US', number: val };
}

/** Resolve the initial dial-prefix country from the value or the default. */
function initialCode(value: string, defaultCountry?: string): string {
  if (value) return parsePhoneValue(value).code;
  if (defaultCountry && countryDialList.some((c) => c.code === defaultCountry)) {
    return defaultCountry;
  }
  return 'US';
}

export function PhoneInput({ value, onChange, placeholder, className, hasError, defaultCountry }: PhoneInputProps) {
  const t = useT();
  const localizedDialList = getCountryDialList(useWidgetLanguage());
  const [selectedCode, setSelectedCode] = useState(() => initialCode(value, defaultCountry));
  const [phoneNumber, setPhoneNumber] = useState(() => parsePhoneValue(value).number);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedCountry = useMemo(
    () => localizedDialList.find((c) => c.code === selectedCode) || localizedDialList[0],
    [localizedDialList, selectedCode]
  );

  const filtered = useMemo(() => {
    if (!search) return localizedDialList;
    const q = normalizeForSearch(search);
    return localizedDialList.filter(
      (c) =>
        normalizeForSearch(c.name).includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.includes(q)
    );
  }, [localizedDialList, search]);

  const updateValue = useCallback(
    (code: string, number: string) => {
      const country = countryDialList.find((c) => c.code === code);
      const dial = country?.dial || '';
      onChange(number ? `${dial} ${number}` : '');
    },
    [onChange]
  );

  const handleCountrySelect = useCallback(
    (code: string) => {
      setSelectedCode(code);
      setIsOpen(false);
      setSearch('');
      updateValue(code, phoneNumber);
    },
    [phoneNumber, updateValue]
  );

  const handleNumberChange = useCallback(
    (num: string) => {
      setPhoneNumber(num);
      updateValue(selectedCode, num);
    },
    [selectedCode, updateValue]
  );

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (!eventPathContains(e, containerRef.current)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  return (
    <div className={`${styles.container} ${className || ''}`} ref={containerRef}>
      <div className={`${styles.inputRow} ${hasError ? styles.hasError : ''}`}>
        <button
          type="button"
          className={styles.prefixButton}
          onClick={() => {
            setIsOpen(!isOpen);
            setSearch('');
            if (!isOpen) requestAnimationFrame(() => searchInputRef.current?.focus());
          }}
        >
          <span className={styles.flag}>{selectedCountry.flag}</span>
          <span className={styles.dial}>{selectedCountry.dial}</span>
          <svg className={styles.chevron} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <input
          type="tel"
          value={phoneNumber}
          onChange={(e) => handleNumberChange(e.target.value)}
          placeholder={placeholder || t('form.phoneNumber')}
          className={styles.phoneInput}
        />
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrapper}>
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('form.searchCountries')}
              className={styles.searchInput}
            />
          </div>
          <ul className={styles.optionsList}>
            {filtered.length === 0 ? (
              <li className={styles.noResults}>{t('form.noCountries')}</li>
            ) : (
              filtered.map((country) => (
                <li key={country.code}>
                  <button
                    type="button"
                    className={`${styles.option} ${country.code === selectedCode ? styles.optionSelected : ''}`}
                    onClick={() => handleCountrySelect(country.code)}
                  >
                    <span className={styles.flag}>{country.flag}</span>
                    <span className={styles.optionName}>{country.name}</span>
                    <span className={styles.optionDial}>{country.dial}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
